import React, { FC } from 'react';
import Tooltip from 'react-tooltip';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Decision, EntityType } from '../entity.types';
import { numberWithSpaces } from '../../utils/math';
import { EntityInfos } from '../expertises/infos/EntityInfos';
import { HyperlinkRestrictedBaseModal } from '../../components/HyperlinkRestrictedBaseModal';
import { AnimatedLink } from '../../components/animated-link/AnimatedLink';
import { MaterialIcon } from '../../components/material-icon/MaterialIcon';
import styles from './entity_top_decisions.module.scss';
import { InjectedIntlProps } from '../../doctrine.types';
import {
  EntityBlock,
  EntityBlockTitle,
} from '../components/EntityBlockComponents';

type Props = {
  decisions: Array<Decision>;
  entityType: EntityType;
  allDecisionLink: string;
  decisionCount: number;
  lawyerId?: string;
  entityName: string;
  dontShowHref?: boolean;
  sources: {
    allDecisionsLink: string;
    decisionLink: string;
  };
} & InjectedIntlProps;

export const RawEntityTopDecisions: FC<Props> = ({
  decisions,
  allDecisionLink,
  decisionCount,
  lawyerId = '',
  entityName,
  entityType,
  sources,
  intl,
  dontShowHref,
}) => {
  const winnerWording = {
    COSTS: (
      <FormattedMessage
        id="entity/components/top-decisions/TopDecisions.tsx/1"
        values={{
          part: (
            <b>
              <FormattedMessage id="entity/components/top-decisions/TopDecisions.tsx/2" />
            </b>
          ),
        }}
      />
    ),
    SOLUTION: (
      <FormattedMessage
        id="entity/components/top-decisions/TopDecisions.tsx/3"
        values={{
          part: (
            <b>
              <FormattedMessage id="entity/components/top-decisions/TopDecisions.tsx/4" />
            </b>
          ),
        }}
      />
    ),
  };

  const loserWording = {
    COSTS: (
      <FormattedMessage
        id="entity/components/top-decisions/TopDecisions.tsx/5"
        values={{
          part: (
            <b>
              <FormattedMessage id="entity/components/top-decisions/TopDecisions.tsx/6" />
            </b>
          ),
        }}
      />
    ),
    SOLUTION: (
      <FormattedMessage
        id="entity/components/top-decisions/TopDecisions.tsx/7"
        values={{
          part: (
            <b>
              <FormattedMessage id="entity/components/top-decisions/TopDecisions.tsx/8" />
            </b>
          ),
        }}
      />
    ),
  };

  return (
    <EntityBlock>
      <EntityBlockTitle>
        <FormattedMessage
          id="entity/components/top-decisions/TopDecisions.tsx/11"
          values={{ count: decisions.length }}
        />
      </EntityBlockTitle>
      <EntityInfos
        decisionCount={decisionCount}
        entityType={entityType}
        lawyerId={lawyerId}
        entityName={entityName}
      />
      {decisions.map((decision) => (
        <div key={decision.referenceUrl} className={styles.container}>
          <HyperlinkRestrictedBaseModal
            url={decision.referenceUrl}
            source={sources.decisionLink}
            className={styles.item}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: decision.titre,
              }}
            />
          </HyperlinkRestrictedBaseModal>
          {typeof decision.winner === 'boolean' && (
            <div>
              <span className={styles.label}>
                {decision.winner
                  ? winnerWording[decision.rule]
                  : loserWording[decision.rule]}
              </span>
              <span
                data-tip={intl.formatMessage({
                  id: 'entity/components/top-decisions/TopDecisions.tsx/10',
                })}
                data-for="top-decisions"
              >
                <MaterialIcon name="info_outline" className={styles.icon} />
              </span>
            </div>
          )}
        </div>
      ))}
      <AnimatedLink
        href={dontShowHref ? undefined : allDecisionLink}
        source={sources.allDecisionsLink}
      >
        <FormattedMessage
          id="entity/components/top-decisions/TopDecisions.tsx/9"
          values={{ number: numberWithSpaces(decisionCount) }}
        />
      </AnimatedLink>
      <Tooltip id="top-decisions" effect="solid" />
    </EntityBlock>
  );
};

export const EntityTopDecisions = injectIntl(RawEntityTopDecisions);
